import { MarketIntelligencePages } from '@/routes/MarketIntelligencePages'
import { MenusPageTabs } from '@/routes/MenusPageTabs'
import { PATHS } from '@/routes/PATHS'
import { generatePath, matchPath } from 'react-router'

/**
 * Checks if pathname starts with any of the provided paths and returns the destination.
 *
 * @param params - Object containing pathname, paths to check, and destination
 * @param params.pathname - The current location pathname
 * @param params.paths - Array of paths to check against
 * @param params.destination - The result to return if any path matches
 * @returns The destination if pathname starts with any path, undefined otherwise
 */
function startsWithAny({
  pathname,
  paths,
  destination,
}: {
  pathname: string
  paths: string[]
  destination: string
}): string | undefined {
  return paths.some((path) => !!matchPath({ path, end: false }, pathname)) ? destination : undefined
}

/**
 * Determines the active sidebar key(s) based on the current pathname and returns the destination.
 * Individual cases need to be manually added as a condition
 *
 * Example:
 * - If the pathname is '/reviews/review-analysis', the destination is '/reviews/review-analysis'
 * - If the pathname is '/reviews/sentiment-analysis', the destination is '/reviews/sentiment-analysis'
 * - If the pathname is '/notifications/123', the destination is '/notifications'
 *
 * To do that, add the PATHS necessary and use the startsWithAny function.
 * Example:
 *
 * const isMenuItem = startsWithAny({
 *   pathname,
 *   paths: [PATHS[MenusPageTabs.sectionsPage], PATHS[MenusPageTabs.itemsPage], PATHS[MenusPageTabs.modifiersPage], PATHS[MenusPageTabs.choicesPage], PATHS.menusPage],
 *   destination: PATHS[MenusPageTabs.menusPage],
 * })
 * if (isMenuItem) {
 *   return [isMenuItem]
 * }
 *
 * @param pathname - The current location pathname
 * @returns The destination if the pathname starts with any of the paths, undefined otherwise
 */
export function getActiveSidebarKey(pathname: string): string[] {
  const pathOnly = pathname.replace(/[?#].*$/, '')
  const isMenuItem = startsWithAny({
    pathname: pathOnly,
    paths: [
      PATHS[MenusPageTabs.sectionsPage],
      PATHS[MenusPageTabs.itemsPage],
      PATHS[MenusPageTabs.modifiersPage],
      PATHS[MenusPageTabs.choicesPage],
      PATHS.menusPage,
    ],
    destination: PATHS[MenusPageTabs.menusPage],
  })
  if (isMenuItem) {
    return [isMenuItem]
  }

  const isInsightItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.reviewAnalysis, PATHS.sentimentAnalysis, PATHS.keywordAnalysis],
    destination: PATHS.reviewAnalysis,
  })
  if (isInsightItem) {
    return [isInsightItem]
  }

  const isMarketIntelligenceItem = startsWithAny({
    pathname: pathOnly,
    paths: [
      PATHS[MarketIntelligencePages.locationsTable],
      PATHS[MarketIntelligencePages.competitorsByLocations],
    ],
    destination: PATHS[MarketIntelligencePages.locationsTable],
  })
  if (isMarketIntelligenceItem) {
    return [isMarketIntelligenceItem]
  }

  const isFirstPartyItem = startsWithAny({
    pathname: pathOnly,
    paths: [
      PATHS.firstPartyReviewsFte,
      PATHS.firstPartyReviews,
      PATHS.firstPartyReviewsForm,
      PATHS.firstPartyReviewsUpsell,
    ],
    destination: PATHS.firstPartyReviews,
  })
  if (isFirstPartyItem) {
    return [isFirstPartyItem]
  }

  const isResponsePromptsItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.reviewResponsePromptsPage, PATHS.reviewResponsePrompt],
    destination: PATHS.reviewResponsePromptsPage,
  })
  if (isResponsePromptsItem) {
    return [isResponsePromptsItem]
  }

  const isTemplatesItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.reviewTemplatesPage, PATHS.reviewTemplatePage],
    destination: PATHS.reviewTemplatesPage,
  })
  if (isTemplatesItem) {
    return [isTemplatesItem]
  }

  const isNotificationsItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.reviewNotificationsPage, PATHS.reviewNotification],
    destination: PATHS.reviewNotificationsPage,
  })
  if (isNotificationsItem) {
    return [isNotificationsItem]
  }

  const isSocialManagementItem = startsWithAny({
    pathname: pathOnly,
    paths: [
      PATHS.publishedTab,
      PATHS.scheduledTab,
      PATHS.draftsTab,
      PATHS.choosePublisher,
      PATHS.createFacebookPost,
      PATHS.createGooglePost,
      PATHS.createApplePost,
      PATHS.editPost,
    ],
    destination: PATHS.publishedTab,
  })
  if (isSocialManagementItem) {
    return [isSocialManagementItem]
  }

  const isWidgetsItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.widgetsList, PATHS.widgetFte, PATHS.hourWidget, PATHS.menuWidget],
    destination: PATHS.widgetsList,
  })
  if (isWidgetsItem) {
    return [isWidgetsItem]
  }

  const isReportsItem = startsWithAny({
    pathname: pathOnly,
    paths: [PATHS.emailReportsPage, PATHS.emailReport],
    destination: PATHS.emailReportsPage,
  })
  if (isReportsItem) {
    return [isReportsItem]
  }

  const isIntegrationPOSItem = startsWithAny({
    pathname: pathOnly,
    paths: [
      PATHS.toastConnectionSetup,
      PATHS.toastConnectionResult,
      PATHS.toastConnectionDetails,
      PATHS.toastConnectionCreate,
    ],
    destination: PATHS.accountConnectionsPage,
  })
  if (isIntegrationPOSItem) {
    return [isIntegrationPOSItem]
  }

  // Fallback: match against all paths
  const allPaths = Object.values(PATHS)
  const keys: string[] = []

  allPaths.forEach((path) => {
    const match = matchPath({ path, end: true }, pathOnly)

    if (match) {
      const paramKeys = Object.keys(match.params) as (keyof typeof match.params)[]
      const params = paramKeys.reduce(
        (acc, key) => {
          acc[key] = ''
          return acc
        },
        {} as Record<keyof typeof match.params, string | null>,
      )
      const generatedPath = generatePath(path, params)

      if (generatedPath !== '') {
        keys.push(generatedPath)
      }
    }
  })

  return keys.length > 0 ? keys : [pathOnly]
}
